import React, { Component, type ReactNode } from 'react';
import { getLogger } from '@/lib/logger';
import { ErrorPage } from '@/pages/ErrorPage';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  showDialog?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error Boundary component
 * Catches React errors and sends them to Sentry
 *
 * Usage:cr
 * <ErrorBoundary>
 *   <YourApp />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error using logger service
    const logger = getLogger();
    if (logger.captureException) {
      logger.captureException(error, {
        componentStack: errorInfo.componentStack,
      });
    } else {
      logger.error('React Error Boundary caught an error', {
        error: error.message,
        errorName: error.name,
        componentStack: errorInfo.componentStack,
        stack: error.stack,
      });
    }
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return <ErrorPage onReload={this.handleReload} />;
    }

    return this.props.children;
  }
}
